import React, { useState, useRef, forwardRef, useImperativeHandle, Fragment } from 'react';
import { debounce, noop } from 'lodash';
import { Card, Header } from 'semantic-ui-react';
import AsyncCreatableSelect from 'react-select/async-creatable';
import { FormattedMessage, useIntl } from 'react-intl';

// STYLES
import { ACTIVE } from 'styles/commonClasses';
import styles from './SearchableCard.styles.less';
import {
  CustomOptionsContainer,
  OptionAddress,
  OptionContainer,
  OptionCreate,
  OptionEmail,
  OptionName,
  OptionPhone,
} from './SearchCreatable.styles';

// TYPES
import { Customer } from 'services/resources';

type NewCustomerOption = {
  id: null;
  firstName: string;
  lastName: string;
  email: string;
  phoneCell: string;
  address1: string;
};

type SearchableCreatableCardProps = {
  header?: React.ReactNode;
  isDisabled?: boolean;
  onSelect: (option: Customer | NewCustomerOption) => void;
  onSearch: (inputValue: string) => Promise<Customer[]>;
  children?: React.ReactNode;
  isOptionSelected: boolean;
  setIsOptionSelected: (isSelected: boolean) => void;
};

const SearchableCreatableCard = forwardRef(
  (
    {
      header,
      isDisabled,
      onSelect,
      onSearch,
      children,
      isOptionSelected,
      setIsOptionSelected,
    }: SearchableCreatableCardProps,
    ref
  ) => {
    const [showSearchContent, setShowSearchContent] = useState(false);

    const selectRef = useRef<AsyncCreatableSelect<any, boolean, any>>(null);
    const intl = useIntl();

    // Expose focus method to parent
    useImperativeHandle(ref, () => ({
      focus: () => {
        if (selectRef.current) {
          selectRef.current.focus();
        }
      },
    }));

    const handleToggleSearchContent = debounce((show) => {
      setShowSearchContent(show);
    }, 300);

    const loadOptions = async (inputValue: string) => {
      const searched = await onSearch(inputValue);
      return searched;
    };

    // Custom Option component with columns
    const CustomOption = (props) => {
      const { data, innerRef, innerProps, isFocused, value } = props;
      return (
        <CustomOptionsContainer ref={innerRef} {...innerProps} isFocused={isFocused}>
          {data.id ? (
            <OptionContainer ref={innerRef} {...innerProps} isFocused={isFocused}>
              <OptionName>
                {data.firstName} {data.lastName}
              </OptionName>
              <OptionEmail>{data.email}</OptionEmail>
              <OptionPhone>{data.phoneCell}</OptionPhone>
              <OptionAddress>{data.address1}</OptionAddress>
            </OptionContainer>
          ) : (
            <OptionCreate isFocused={isFocused}>
              <FormattedMessage id="agreementModal.customer.create" values={{ value }} />
            </OptionCreate>
          )}
        </CustomOptionsContainer>
      );
    };

    // Custom styles for better column layout
    const customStyles = {
      control: (base) => ({
        ...base,
        borderRadius: '4px',
        borderColor: '#ddd',
        '&:hover': { borderColor: '#aaa' },
        boxShadow: 'none',
      }),
      menu: (base) => ({
        ...base,
        marginTop: '0px',
        borderRadius: '4px',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
      }),
      menuList: (base) => ({
        ...base,
        padding: '0',
      }),
      option: (base) => ({
        ...base,
        padding: 0, // Remove default padding since CustomOption handles it
      }),
    };

    const handleCreateOption = (newValue) => {
      // Handle creation of a new option
      const isPhone = /^\+?[1-9]\d{1,14}$/.test(newValue);

      if (isPhone) {
        onSelect({ id: null, firstName: '', lastName: '', email: '', phoneCell: `+1 ${newValue}`, address1: '' });
      } else {
        onSelect({ id: null, firstName: newValue, lastName: '', email: '', phoneCell: '', address1: '' });
      }
      setIsOptionSelected(true);
    };

    const handleOnSelect = (selectedOption: Customer) => {
      onSelect(selectedOption);
      setIsOptionSelected(true);
    };

    return (
      <div className={`${styles.searchableCard__wrapper} searchable-card-wrapper`}>
        <Card className={showSearchContent ? ACTIVE : undefined}>
          {header}
          {!isOptionSelected && (
            <>
              {!isDisabled && (
                <div className={`${styles.searchableCard__headerWrapper} searchable-card-header-wrapper`}>
                  <div className={`${styles.searchableCard__searchWrapper} searchable-card-search-wrapper`}>
                    <AsyncCreatableSelect
                      ref={selectRef}
                      loadOptions={loadOptions}
                      onChange={handleOnSelect}
                      onCreateOption={handleCreateOption}
                      noOptionsMessage={() => intl.formatMessage({ id: 'agreementModal.customer.noOptions' })}
                      onBlur={() => handleToggleSearchContent(false)}
                      onFocus={() => handleToggleSearchContent(true)}
                      styles={customStyles}
                      components={{ Option: CustomOption }}
                      placeholder={<FormattedMessage id="search.placeholder.agreementCustomer" />}
                      cacheOptions
                    />
                  </div>
                </div>
              )}

              <div>
                <Fragment>
                  <Header as="h4" textAlign="center" disabled>
                    <FormattedMessage id="views.Agreement.selectCustomerPlaceholder.line1" />
                    <br />
                    <FormattedMessage id="views.Agreement.selectCustomerPlaceholder.line2" />
                  </Header>
                </Fragment>
              </div>
            </>
          )}

          <Card.Content style={{ marginTop: isDisabled || isOptionSelected ? 8 : undefined }}>{children}</Card.Content>
        </Card>
      </div>
    );
  }
);

export default SearchableCreatableCard;
